// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import styles from './addCandidate.module.scss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PageLoader from '../../../../components/PageLoader';
import {
  parseCandidates,
  confirmCandidates,
  clearParseCandidatesState,
  clearConfirmCandidatesState,
  fetchOpenJobs,
  type CandidateDraft,
  type ConfirmCandidatePayload
} from '../../../../store/jobManagementSlice';

type UploadMode = 'single' | 'multiple';

interface DraftWithAction extends CandidateDraft {
  action: 'confirm' | 'confirm_separate' | 'remove';
  expanded?: boolean;
  interview_context?: string;
}

const AddCandidate: React.FC = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const dispatch = useAppDispatch();
  const {
    openJobs,
    openJobsLoading,
    parseCandidatesLoading,
    parseCandidatesError,
    parseCandidatesSuccess,
    confirmCandidatesLoading,
    confirmCandidatesError,
    confirmCandidatesSuccess
  } = useSelector((state: RootState) => state.jobManagement);

  // Pre-select jobId from URL params
  const [selectedJobId, setSelectedJobId] = useState<string>(jobId || '');
  const [uploadMode, setUploadMode] = useState<UploadMode>('multiple');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [drafts, setDrafts] = useState<DraftWithAction[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'review'>('upload');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, Record<string, string>>>({});
  const [uploadFieldMessage, setUploadFieldMessage] = useState<string | null>(null);

  const validateDrafts = useCallback((draftsToValidate: DraftWithAction[]): Record<string, Record<string, string>> => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors: Record<string, Record<string, string>> = {};

    draftsToValidate.forEach((draft) => {
      const draftErrors: Record<string, string> = {};

      if (!draft.candidate.name?.trim()) {
        draftErrors.name = 'Name is required';
      }
      if (!draft.candidate.email?.trim()) {
        draftErrors.email = 'Email is required';
      } else if (!emailRegex.test(draft.candidate.email.trim())) {
        draftErrors.email = 'Please enter a valid email address';
      }
      if (!draft.candidate.phone?.trim()) {
        draftErrors.phone = 'Phone is required';
      }
      if (!draft.candidate.current_title?.trim()) {
        draftErrors.current_title = 'Current title is required';
      }
      if (!draft.candidate.location?.trim()) {
        draftErrors.location = 'Location is required';
      }
      if (
        draft.candidate.experience_years === null ||
        draft.candidate.experience_years === undefined ||
        draft.candidate.experience_years < 0
      ) {
        draftErrors.experience_years = 'Experience years is required';
      }

      if (Object.keys(draftErrors).length > 0) {
        errors[draft.draft_id] = draftErrors;
      }
    });

    return errors;
  }, []);

  // Fetch open jobs on mount
  useEffect(() => {
    dispatch(fetchOpenJobs());
  }, [dispatch]);

  // Handle parse success - move to review step
  useEffect(() => {
    if (parseCandidatesSuccess) {
      // First pass: sanitize and validate drafts
      const initialDrafts: DraftWithAction[] = parseCandidatesSuccess.drafts.map((draft) => ({
        ...draft,
        // Sanitize candidate data from parsed resume
        candidate: {
          ...draft.candidate,
          // Ensure experience_years is never negative (server may parse negative values)
          experience_years: Math.max(0, draft.candidate.experience_years ?? 0)
        },
        action: draft.duplicate_candidate.flagged ? 'remove' : 'confirm',
        expanded: false, // Will be set based on validation
        interview_context: ''
      }));

      const errors = validateDrafts(initialDrafts);

      // Second pass: auto-expand accordions with errors or duplicates
      const draftsWithActions: DraftWithAction[] = initialDrafts.map((draft) => ({
        ...draft,
        expanded:
          draft.duplicate_candidate.flagged || draft.status === 'needs_review' || Boolean(errors[draft.draft_id]) // Auto-expand if has validation errors
      }));

      setDrafts(draftsWithActions);
      setCurrentStep('review');

      // Set field errors if any
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
      }
    }
  }, [parseCandidatesSuccess, validateDrafts]);

  // Handle confirm success - show success message and reset
  useEffect(() => {
    if (confirmCandidatesSuccess) {
      const { created_count, removed_count, failed_count, errors } = confirmCandidatesSuccess;

      let successMsg = '';
      let warningMsg = '';

      if (created_count > 0) {
        successMsg = `Successfully added ${created_count} candidate${created_count !== 1 ? 's' : ''} and screening is in progress!`;
      }

      if (removed_count > 0 || failed_count > 0) {
        const parts = [];
        if (removed_count > 0) {
          parts.push(`${removed_count} removed`);
        }
        if (failed_count > 0) {
          const errorDetails =
            errors && errors.length > 0 ? errors.map((e: any) => e.message || e.detail).join(', ') : 'Unknown error';
          parts.push(`${failed_count} failed: ${errorDetails}`);
        }
        warningMsg = parts.join(', ');
      }

      setSuccessMessage(successMsg || null);
      setWarningMessage(warningMsg || null);

      // Reset to initial state after successful confirmation
      setCurrentStep('upload');
      setSelectedFiles([]);
      setDrafts([]);
      setFieldErrors({});
      dispatch(clearParseCandidatesState());
      dispatch(clearConfirmCandidatesState());

      // At least one candidate was added successfully - head back to the
      // job's candidates list instead of leaving the recruiter on this page.
      if (created_count > 0 && selectedJobId) {
        navigate(`/jobs/${selectedJobId}/candidates`, { state: { flashMessage: successMsg } });
        return;
      }

      // Auto-dismiss messages after 10 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setWarningMessage(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [confirmCandidatesSuccess, dispatch, navigate, selectedJobId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearParseCandidatesState());
      dispatch(clearConfirmCandidatesState());
    };
  }, [dispatch]);

  // Auto-dismiss warning messages after 5 seconds
  useEffect(() => {
    if (warningMessage) {
      const timer = setTimeout(() => setWarningMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage]);

  const handleJobChange = useCallback((jobId: string): void => {
    setSelectedJobId(jobId);
  }, []);

  const handleModeChange = useCallback((mode: UploadMode): void => {
    setUploadMode(mode);
    if (mode === 'single') {
      setSelectedFiles((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback(
    (files: File[]): void => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB
      const maxFiles = 20;

      const validFiles: File[] = [];
      const errors: string[] = [];

      files.forEach((file) => {
        if (!validTypes.includes(file.type)) {
          errors.push(`${file.name}: Only PDF and DOC/DOCX files are allowed`);
        } else if (file.size > maxSize) {
          errors.push(`${file.name}: File size must be less than 10MB`);
        } else {
          validFiles.push(file);
        }
      });

      if (errors.length > 0) {
        setWarningMessage(errors.join('\n'));
      }

      if (uploadMode === 'single') {
        setSelectedFiles(validFiles.slice(0, 1));
      } else {
        setSelectedFiles((prev) => {
          const remainingSlots = maxFiles - prev.length;
          const newFiles = validFiles.slice(0, remainingSlots);

          if (validFiles.length > remainingSlots) {
            setUploadFieldMessage(`Maximum ${maxFiles} files allowed. Only ${remainingSlots} files added.`);
          } else if (prev.length + newFiles.length >= maxFiles) {
            setUploadFieldMessage(`Maximum limit of ${maxFiles} files reached.`);
          } else {
            setUploadFieldMessage(null);
          }

          return [...prev, ...newFiles];
        });
      }
    },
    [uploadMode]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(Array.from(e.dataTransfer.files));
      }
    },
    [handleFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(Array.from(e.target.files));
      }
    },
    [handleFiles]
  );

  const handleRemoveFile = useCallback((index: number): void => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Clear message if we're below the max limit after removal
      if (updated.length < 20) {
        setUploadFieldMessage(null);
      }
      return updated;
    });
  }, []);

  const handleUploadAndParse = useCallback(async (): Promise<void> => {
    if (!selectedJobId) {
      setWarningMessage('Please select a job role');
      return;
    }

    if (selectedFiles.length === 0) {
      setWarningMessage('Please upload at least one resume');
      return;
    }

    dispatch(parseCandidates({ job_id: selectedJobId, cv_files: selectedFiles }));
  }, [selectedJobId, selectedFiles, dispatch]);

  const toggleDraftExpanded = useCallback((draftId: string): void => {
    setDrafts((prev) =>
      prev.map((draft) => (draft.draft_id === draftId ? { ...draft, expanded: !draft.expanded } : draft))
    );
  }, []);

  const handleDraftAction = useCallback(
    (draftId: string, action: 'confirm' | 'confirm_separate' | 'remove'): void => {
      let draftToValidate: DraftWithAction | undefined;

      // Pure updater: compute and capture the draft that needs validation
      setDrafts((prev) => {
        const updated = prev.map((draft) => {
          if (draft.draft_id === draftId) {
            const updatedDraft = { ...draft, action };
            // Capture the updated draft for validation outside the updater
            if (action !== 'remove') {
              draftToValidate = updatedDraft;
            }
            return updatedDraft;
          }
          return draft;
        });
        return updated;
      });

      // Separate side effect: update validation errors based on action
      if (action === 'remove') {
        // Clear validation errors for removed drafts
        setFieldErrors((prevErrors) => {
          if (!prevErrors[draftId]) return prevErrors;
          const { [draftId]: removed, ...remaining } = prevErrors;
          return remaining;
        });
      } else if (draftToValidate) {
        // Validate draft marked for confirmation (compute outside setState)
        const validationErrors = validateDrafts([draftToValidate]);
        const hasErrors = Object.keys(validationErrors).length > 0;

        // Update field errors based on validation result
        setFieldErrors((prevErrors) => {
          if (hasErrors) {
            // Add new errors
            return { ...prevErrors, ...validationErrors };
          } else if (prevErrors[draftId]) {
            // Clear errors if validation passes
            const { [draftId]: removed, ...remaining } = prevErrors;
            return remaining;
          }
          return prevErrors;
        });
      }
    },
    [validateDrafts]
  );

  const handleCandidateFieldChange = useCallback((draftId: string, field: string, value: any): void => {
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.draft_id === draftId ? { ...draft, candidate: { ...draft.candidate, [field]: value } } : draft
      )
    );

    // Clear error for this field when user starts typing
    setFieldErrors((prev) => {
      if (!prev[draftId]?.[field]) return prev;

      const updated = { ...prev };
      if (updated[draftId]) {
        const { [field]: removed, ...rest } = updated[draftId];
        if (Object.keys(rest).length === 0) {
          delete updated[draftId];
        } else {
          updated[draftId] = rest;
        }
      }
      return updated;
    });
  }, []);

  const handleInterviewContextChange = useCallback((draftId: string, value: string): void => {
    setDrafts((prev) =>
      prev.map((draft) => (draft.draft_id === draftId ? { ...draft, interview_context: value } : draft))
    );
  }, []);

  const handleConfirmCandidates = useCallback(async (): Promise<void> => {
    if (!parseCandidatesSuccess) return;

    const errors = validateDrafts(drafts);
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      setFieldErrors(errors);
      setWarningMessage('Please fill in all required fields');
      return;
    }

    // Clear errors if validation passes
    setFieldErrors({});

    const confirmPayload: ConfirmCandidatePayload[] = drafts.map((draft) => ({
      draft_id: draft.draft_id,
      action: draft.action,
      candidate: {
        name: draft.candidate.name.trim(),
        email: draft.candidate.email.trim(),
        phone: draft.candidate.phone.trim(),
        current_title: draft.candidate.current_title.trim(),
        location: draft.candidate.location.trim(),
        experience_years: draft.candidate.experience_years,
        skills: draft.candidate.skills.filter((s) => s.trim()),
        resume_summary: draft.candidate.resume_summary.trim(),
        interview_context: (draft as any).interview_context?.trim() || ''
      }
    }));

    dispatch(
      confirmCandidates({
        job_id: selectedJobId,
        request: {
          parse_batch_id: parseCandidatesSuccess.parse_batch_id,
          candidates: confirmPayload
        }
      })
    );
  }, [parseCandidatesSuccess, drafts, selectedJobId, dispatch, validateDrafts]);

  const handleCancel = useCallback((): void => {
    if (selectedJobId) {
      navigate(`/jobs/${selectedJobId}/candidates`);
    } else {
      navigate('/jobs');
    }
  }, [selectedJobId, navigate]);

  const handleBack = useCallback((): void => {
    setCurrentStep('upload');
    setDrafts([]);
    setFieldErrors({});
    dispatch(clearParseCandidatesState());
  }, [dispatch]);

  // Compute derived values (must be before any conditional returns)
  const selectedJob = useMemo(() => openJobs.find((job) => job.job_id === selectedJobId), [openJobs, selectedJobId]);

  const confirmedCount = useMemo(
    () => drafts.filter((d) => d.action === 'confirm' || d.action === 'confirm_separate').length,
    [drafts]
  );

  const removedCount = useMemo(() => drafts.filter((d) => d.action === 'remove').length, [drafts]);

  const actionableCount = useMemo(() => confirmedCount + removedCount, [confirmedCount, removedCount]);

  // Check if all validations pass for drafts that will be confirmed
  const hasValidationErrors = useMemo(() => {
    const draftsToConfirm = drafts.filter((d) => d.action === 'confirm' || d.action === 'confirm_separate');
    const errors = validateDrafts(draftsToConfirm);
    return Object.keys(errors).length > 0;
  }, [drafts, validateDrafts]);

  // Early return after all hooks are called
  if (openJobsLoading) {
    return <PageLoader label="Loading open jobs..." activeNav="home" showSidebar={false} />;
  }

  return (
    <div className={styles.container}>
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={() => navigate('/')}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={() => navigate('/jobs')}>
            Jobs
          </button>
          {selectedJob && (
            <>
              <ChevronRightIcon className={styles.breadcrumbSeparator} />
              <button
                className={styles.breadcrumbLink}
                onClick={() => navigate(`/jobs/${selectedJob.job_id}/candidates`)}
              >
                {selectedJob.title}
              </button>
            </>
          )}
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>Add Candidate</span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Add Candidate</h1>
          <p className={styles.subtitle}>
            {currentStep === 'upload'
              ? 'Upload resume(s) to auto-extract candidate details'
              : `Review ${drafts.length} parsed candidate${drafts.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className={styles.successBanner}>
            <div className={styles.successContent}>
              <span className={styles.successIcon}>✓</span>
              <span>{successMessage}</span>
            </div>
            <button className={styles.closeBannerButton} onClick={() => setSuccessMessage(null)}>
              ×
            </button>
          </div>
        )}

        {/* Warning Message */}
        {warningMessage && (
          <div className={styles.warningBanner}>
            <div className={styles.warningContent}>
              <span className={styles.warningIcon}>⚠</span>
              <span style={{ whiteSpace: 'pre-line' }}>{warningMessage}</span>
            </div>
            <button className={styles.closeBannerButton} onClick={() => setWarningMessage(null)}>
              ×
            </button>
          </div>
        )}

        {/* Error Messages */}
        {(parseCandidatesError || confirmCandidatesError) && (
          <div className={styles.errorBanner}>{parseCandidatesError || confirmCandidatesError}</div>
        )}

        {/* Step 1: Upload */}
        {currentStep === 'upload' && (
          <div className={styles.uploadStep}>
            {/* Job Selection */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.stepNumber}>1</span>
                <span className={styles.sectionTitle}>SELECT JOB ROLE</span>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Job role <span className={styles.required}>*</span>
                </label>
                <select
                  className={styles.select}
                  value={selectedJobId}
                  onChange={(e) => handleJobChange(e.target.value)}
                  disabled={Boolean(jobId)}
                  title={jobId ? 'Job role is fixed because you opened Add Candidate from this job.' : undefined}
                >
                  <option value="">{openJobs.length === 0 ? 'No open jobs available' : 'Select job role'}</option>
                  {openJobs.map((job) => (
                    <option key={job.job_id} value={job.job_id}>
                      {job.title} ({job.department} · {job.location})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Upload Mode Selection */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.stepNumber}>2</span>
                <span className={styles.sectionTitle}>UPLOAD RESUME(S)</span>
              </div>

              <div className={styles.modeSelector}>
                <button
                  type="button"
                  className={`${styles.modeCard} ${uploadMode === 'single' ? styles.modeCardActive : ''}`}
                  onClick={() => handleModeChange('single')}
                >
                  <div className={styles.modeRadio}>
                    {uploadMode === 'single' && <div className={styles.modeRadioChecked} />}
                  </div>
                  <div className={styles.modeContent}>
                    <h4 className={styles.modeTitle}>Single candidate</h4>
                    <p className={styles.modeDescription}>Upload one resume</p>
                  </div>
                </button>

                <button
                  type="button"
                  className={`${styles.modeCard} ${uploadMode === 'multiple' ? styles.modeCardActive : ''}`}
                  onClick={() => handleModeChange('multiple')}
                >
                  <div className={styles.modeRadio}>
                    {uploadMode === 'multiple' && <div className={styles.modeRadioChecked} />}
                  </div>
                  <div className={styles.modeContent}>
                    <h4 className={styles.modeTitle}>Multiple candidates</h4>
                    <p className={styles.modeDescription}>Upload up to 20 resumes at once</p>
                  </div>
                </button>
              </div>

              {/* Upload Area */}
              <div
                className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {selectedFiles.length > 0 ? (
                  <div className={styles.filesContainer}>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                        <div className={styles.fileInfo}>
                          <p className={styles.fileName}>{file.name}</p>
                          <p className={styles.fileSize}>{(file.size / 1024).toFixed(0)} KB</p>
                        </div>
                        <button
                          type="button"
                          className={styles.removeFileButton}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </button>
                      </div>
                    ))}
                    {uploadMode === 'multiple' && selectedFiles.length < 20 && (
                      <label className={styles.addMoreButton}>
                        + Add more resumes
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileInput}
                          multiple
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                  </div>
                ) : (
                  <>
                    <CloudUploadIcon className={styles.uploadIcon} />
                    <p className={styles.uploadText}>
                      Drag and drop up to {uploadMode === 'single' ? '1 resume' : '20 resumes'} here
                    </p>
                    <p className={styles.uploadSubtext}>or</p>
                    <label className={styles.browseButton}>
                      Browse Files
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileInput}
                        multiple={uploadMode === 'multiple'}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <p className={styles.uploadInfo}>
                      PDF, DOC, DOCX · Max 10MB each · Max {uploadMode === 'single' ? '1' : '20'} files per batch
                    </p>
                  </>
                )}
              </div>

              {/* Field-level message for max files */}
              {uploadFieldMessage && <div className={styles.uploadFieldMessage}>{uploadFieldMessage}</div>}

              {selectedFiles.length > 0 && parseCandidatesSuccess && (
                <div className={styles.slotsInfo}>
                  {parseCandidatesSuccess.remaining_slots} of {parseCandidatesSuccess.max_files} slots remaining in this
                  batch.
                </div>
              )}
            </div>

            <div className={styles.actions}>
              <button className={styles.cancelButton} onClick={handleCancel} disabled={parseCandidatesLoading}>
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleUploadAndParse}
                disabled={parseCandidatesLoading || !selectedJobId || selectedFiles.length === 0}
              >
                {parseCandidatesLoading
                  ? 'Parsing resumes...'
                  : `Parse ${selectedFiles.length} resume${selectedFiles.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {currentStep === 'review' && parseCandidatesSuccess && (
          <div className={styles.reviewStep}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.stepNumber}>3</span>
                <span className={styles.sectionTitle}>CANDIDATE DETAILS</span>
              </div>

              <div className={styles.draftsContainer}>
                {drafts.map((draft) => (
                  <div
                    key={draft.draft_id}
                    className={`${styles.draftCard} ${
                      draft.duplicate_candidate.flagged ? styles.draftCardWarning : ''
                    }`}
                  >
                    <div className={styles.draftHeader} onClick={() => toggleDraftExpanded(draft.draft_id)}>
                      <div className={styles.draftHeaderLeft}>
                        <button type="button" className={styles.expandButton}>
                          {draft.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </button>
                        <div className={styles.draftInfo}>
                          <h4 className={styles.draftName}>{draft.candidate.name || 'Unnamed Candidate'}</h4>
                          <p className={styles.draftFileName}>
                            <span className={styles.fileIcon}>📄</span>
                            {draft.file_name}
                          </p>
                        </div>
                      </div>
                      <div className={styles.draftStatus}>
                        {draft.duplicate_candidate.flagged ? (
                          <span className={styles.statusWarning}>
                            <WarningAmberIcon sx={{ fontSize: 16 }} />
                            Possible duplicate
                          </span>
                        ) : draft.status === 'processed' ? (
                          <span className={styles.statusProcessed}>
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                            Processed
                          </span>
                        ) : (
                          <span className={styles.statusProcessing}>
                            <HourglassEmptyIcon sx={{ fontSize: 16 }} />
                            Processing
                          </span>
                        )}
                      </div>
                    </div>

                    {draft.expanded && (
                      <div className={styles.draftBody}>
                        {draft.duplicate_candidate.flagged && (
                          <div className={styles.duplicateWarning}>
                            <WarningAmberIcon />
                            <div>
                              <p className={styles.warningTitle}>{draft.duplicate_candidate.message}</p>
                              <div className={styles.duplicateActions}>
                                <button
                                  type="button"
                                  className={`${styles.btnRemove} ${draft.action === 'remove' ? styles.selected : ''}`}
                                  onClick={() => handleDraftAction(draft.draft_id, 'remove')}
                                >
                                  Remove this file
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.btnConfirmSeparate} ${draft.action === 'confirm_separate' ? styles.selected : ''}`}
                                  onClick={() => handleDraftAction(draft.draft_id, 'confirm_separate')}
                                >
                                  Confirm as separate candidate
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className={styles.candidateFields}>
                          <div className={styles.fieldRow}>
                            <div className={styles.field}>
                              <label>
                                Name <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="text"
                                value={draft.candidate.name}
                                onChange={(e) => handleCandidateFieldChange(draft.draft_id, 'name', e.target.value)}
                                className={fieldErrors[draft.draft_id]?.name ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.name && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].name}</span>
                              )}
                            </div>
                            <div className={styles.field}>
                              <label>
                                Email <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="email"
                                value={draft.candidate.email}
                                onChange={(e) => handleCandidateFieldChange(draft.draft_id, 'email', e.target.value)}
                                className={fieldErrors[draft.draft_id]?.email ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.email && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].email}</span>
                              )}
                            </div>
                          </div>

                          <div className={styles.fieldRow}>
                            <div className={styles.field}>
                              <label>
                                Phone <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="text"
                                value={draft.candidate.phone}
                                onChange={(e) => handleCandidateFieldChange(draft.draft_id, 'phone', e.target.value)}
                                className={fieldErrors[draft.draft_id]?.phone ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.phone && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].phone}</span>
                              )}
                            </div>
                            <div className={styles.field}>
                              <label>
                                Current Title <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="text"
                                value={draft.candidate.current_title}
                                onChange={(e) =>
                                  handleCandidateFieldChange(draft.draft_id, 'current_title', e.target.value)
                                }
                                className={fieldErrors[draft.draft_id]?.current_title ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.current_title && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].current_title}</span>
                              )}
                            </div>
                          </div>

                          <div className={styles.fieldRow}>
                            <div className={styles.field}>
                              <label>
                                Location <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="text"
                                value={draft.candidate.location}
                                onChange={(e) => handleCandidateFieldChange(draft.draft_id, 'location', e.target.value)}
                                className={fieldErrors[draft.draft_id]?.location ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.location && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].location}</span>
                              )}
                            </div>
                            <div className={styles.field}>
                              <label>
                                Years of Experience <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={draft.candidate.experience_years}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                                  // Prevent negative values
                                  const sanitizedValue = Math.max(0, value);
                                  handleCandidateFieldChange(draft.draft_id, 'experience_years', sanitizedValue);
                                }}
                                className={fieldErrors[draft.draft_id]?.experience_years ? styles.inputError : ''}
                              />
                              {fieldErrors[draft.draft_id]?.experience_years && (
                                <span className={styles.errorText}>{fieldErrors[draft.draft_id].experience_years}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {parseCandidatesSuccess && (
                <div className={styles.reviewSummary}>
                  {confirmedCount > 0 && (
                    <span>
                      {confirmedCount} candidate{confirmedCount !== 1 ? 's' : ''} to add
                    </span>
                  )}
                  {removedCount > 0 && <span className={styles.removedText}>{removedCount} removed</span>}
                </div>
              )}
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={handleBack} disabled={confirmCandidatesLoading}>
                Back
              </button>
              <button className={styles.cancelButton} onClick={handleCancel} disabled={confirmCandidatesLoading}>
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleConfirmCandidates}
                disabled={confirmCandidatesLoading || actionableCount === 0 || hasValidationErrors}
              >
                {confirmCandidatesLoading ? 'Adding candidates...' : `Add candidate(s) & run screening`}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AddCandidate;
